// src/api/bonos-flow.js
// ESM puro, sin axios

const API = {
  ITINERARIO_INFO: (id) => `https://desarrollotecnologicoar.com/api7/itinerario_info/${encodeURIComponent(id)}`,
  FINALIZAR_RESERVA: (id) => `https://desarrollotecnologicoar.com/api5/finalizar_reserva/${encodeURIComponent(id)}`,
  REGISTRO_BONOS: `https://desarrollotecnologicoar.com/api5/registro_bonos`,
  REGISTRO_BONOS_DESGLOSE: (bonoId) => `https://desarrollotecnologicoar.com/api5/registro_bonos_desglose/${encodeURIComponent(bonoId)}`,
  APROBAR_BONO: (bonoId) => `https://desarrollotecnologicoar.com/api5/aprobar_bono/${encodeURIComponent(bonoId)}`,
  REPORTE_VALIDADO: (reporteId) => `https://desarrollotecnologicoar.com/api5/reporte_servicio_validado/${encodeURIComponent(reporteId)}`,
};

const to2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const toNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const tarifaPorLocalidad = (loc) => (String(loc).toLowerCase().includes("forá") ? 950 : 475);
const folioYYYYMMNow = () => {
  const d = new Date();
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}${m}`;
};

async function itinerarioFueExtendido(reservaId) {
  try {
    const r = await fetch(API.ITINERARIO_INFO(reservaId));
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const data = await r.json();
    return data?.fue_extendido === true;
  } catch {
    return true; // misma política que tenías en Flutter
  }
}

async function crearBonoYDevolverId({
  reservaId,
  reservaDetails,
  fueExtendido,
  generarBono,
  selectedTipoServicio,
}) {
  try {
    const folio = folioYYYYMMNow();
    const body = {
      reserva_id: reservaId,
      ticket: reservaDetails?.ticket ?? 0,
      status: fueExtendido ? "Pendiente" : "Aprobado",
      nombre_tecnico: reservaDetails?.tecnico ?? "No asignado",
      correo_tecnico: reservaDetails?.correo_tecnico ?? "developer@asiarobotica.com",
      tipo_servicio: generarBono ? selectedTipoServicio : "N/A",
      localidad: reservaDetails?.localidad ?? "Local",
      mes_pago: folio,
    };

    const resp = await fetch(API.REGISTRO_BONOS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (resp.ok) {
      const data = await resp.json().catch(() => null);
      const candidates = [
        data?.id,
        data?.bono_id,
        data?.insertId,
        data?.data?.id,
        typeof data === "number" ? data : null,
        typeof data === "string" ? Number(data) : null,
      ];
      for (const c of candidates) {
        const id = Number(c);
        if (Number.isFinite(id) && id > 0) return id;
      }
    } else {
      console.error("❗ Error creando bono:", resp.status, await resp.text().catch(() => ""));
    }
  } catch (e) {
    console.error("❗ Excepción creando bono:", e);
  }
  return null;
}

async function finalizarReserva(reservaId) {
  const resp = await fetch(API.FINALIZAR_RESERVA(reservaId), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status: "Finalizado" }),
  });
  if (!resp.ok) {
    const msg = await resp.text().catch(() => "");
    throw new Error(`Error al finalizar reserva: ${resp.status} ${msg}`);
  }
}

async function aprobarContandoAntesDeValidar({ bonoId, reservaDetails }) {
  try {
    const detRes = await fetch(API.REGISTRO_BONOS_DESGLOSE(bonoId));
    if (!detRes.ok) {
      console.error("❗ No pude leer desglose:", detRes.status, await detRes.text().catch(() => ""));
      return;
    }
    const det = await detRes.json();

    const reportes = Array.isArray(det?.reportes_servicio) ? det.reportes_servicio : [];
    const noHojas = reportes.length;

    const sanciones = to2(reportes.reduce((acc, r) => acc + toNumber(r?.sancion_puntualidad), 0));

    const localidad = det?.reserva?.localidad ?? reservaDetails?.localidad ?? "Local";
    const cliente = det?.reserva?.empresa ?? reservaDetails?.empresa ?? "";

    const tarifa = tarifaPorLocalidad(localidad);
    const montoAprobado = to2(Math.max(0, noHojas * tarifa - sanciones));
    const folio = folioYYYYMMNow();

    const putBono = await fetch(API.APROBAR_BONO(bonoId), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status: "Aprobado",
        cliente,
        no_hojas: noHojas,
        monto_aprobado: montoAprobado,
        suma_sancion: sanciones,
        localidad,
        mes_pago: folio,
      }),
    });
    if (!putBono.ok) {
      console.error("❗ Error al aprobar bono:", putBono.status, await putBono.text().catch(() => ""));
    }

    for (const r of reportes) {
      const id = Number(r?.id);
      if (!Number.isFinite(id) || id <= 0) continue;

      const res = await fetch(API.REPORTE_VALIDADO(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hoja_validada: true, bonos_id: bonoId }),
      });
      if (!res.ok) {
        console.error("❗ No pude validar hoja", id, res.status, await res.text().catch(() => ""));
      }
    }

    console.log(`✅ Bono ${bonoId} aprobado con ${noHojas} hojas.`);
    return noHojas;
  } catch (e) {
    console.error("❗ Excepción en aprobarContandoAntesDeValidar:", e);
  }
  return null;
}

export default async function manejarUltimoReporte({
  reservaId,
  generarBono,
  selectedTipoServicio,
  reservaDetails,
}) {
  if (!generarBono) return null;

  const fueExtendido = await itinerarioFueExtendido(reservaId);
  const bonoId = await crearBonoYDevolverId({
    reservaId,
    reservaDetails,
    fueExtendido,
    generarBono,
    selectedTipoServicio,
  });

  await finalizarReserva(reservaId);

  let noHojas = null;
  if (!fueExtendido && Number.isFinite(bonoId)) {
    noHojas = await aprobarContandoAntesDeValidar({ bonoId, reservaDetails });
  }

  return { bonoId, fueExtendido, noHojas };
}
